/**
 * The exercise module contract.
 *
 * Build prompt §1 is the single most important instruction in this project:
 *
 *   "Adding a new exercise requires creating exactly one new file and adding
 *    one registration line. Nothing else in the codebase changes."
 *
 * Everything in this file exists to make that true. The rest of the
 * application knows only about "an exercise" generically — never about any
 * specific exercise.
 *
 * THE SPLIT THAT MAKES IT WORK
 * ----------------------------
 * Each exercise file exports one `ExerciseModule` containing two halves:
 *
 *   definition — pure data. No DOM, no browser APIs. The SERVER imports this
 *                to validate parameters, estimate durations, and know which
 *                metric the safety monitor should watch. Safe on both sides.
 *
 *   run        — the actual implementation. Browser only. Imported lazily by
 *                the session runner and never by the server.
 *
 * Both live in the same file, so the one-file rule holds.
 *
 * WHO CONSUMES WHAT
 * -----------------
 *   Prescription builder  reads `params`   -> renders its settings form
 *   Progress charts       read  `metrics`  -> chart without per-exercise code
 *   EMC safety monitor    reads `decay`    -> knows what fatigue looks like
 *   Time estimate         calls `estimateDurationSec()`
 *   Session runner        calls `run()`
 *
 * None of them contain a switch statement on exercise id. If you find yourself
 * writing one, the contract is missing something — extend the contract instead.
 */
/**
 * How these read on screen. Here rather than in the client because the Phase 4
 * runner has to name the same things to the patient, and two lists that mean
 * the same thing drift apart.
 */
export const POSITION_LABELS = {
    seated: 'Seated',
    standing: 'Standing',
    tandem: 'Tandem stance',
};
export const INPUT_MODE_LABELS = {
    keyboard: 'Keyboard',
    mouse: 'Mouse',
    touch: 'Touch screen',
};
/** Human-facing units, so the UI can label values without a lookup table. */
/**
 * What each metric is CALLED, for anything a person reads.
 *
 * The keys are stable identifiers written into every stored result and must
 * never change; these are the words. Kept beside the units because they are the
 * same kind of fact about a metric, and kept in the contract because a screen
 * that invented its own names would eventually disagree with another screen
 * about what a patient's chart is showing.
 */
export const METRIC_LABELS = {
    reaction_time_ms: 'Reaction time',
    accuracy_pct: 'Accuracy',
    variability: 'Consistency',
    completion_pct: 'Completion',
    tracking_error_deg: 'Tracking error',
};
export const METRIC_UNITS = {
    reaction_time_ms: 'ms',
    accuracy_pct: '%',
    variability: 'ms SD',
    completion_pct: '%',
    tracking_error_deg: 'deg',
};
/**
 * For each metric, which direction is clinically better. The charts use this
 * to orient axes and trend arrows without per-exercise knowledge.
 */
export const METRIC_BETTER_DIRECTION = {
    reaction_time_ms: 'lower',
    accuracy_pct: 'higher',
    variability: 'lower',
    completion_pct: 'higher',
    tracking_error_deg: 'lower',
};
export const METRIC_ACCUMULATION = {
    reaction_time_ms: 'per_trial',
    accuracy_pct: 'cumulative',
    variability: 'cumulative',
    completion_pct: 'cumulative',
    tracking_error_deg: 'per_trial',
};
/** Storage value to the unit the doctor reads. */
export function toDisplayValue(spec, stored) {
    if (!spec.displayUnit)
        return stored;
    return round(stored / spec.displayUnit.factor);
}
/** Back the other way, on input. */
export function fromDisplayValue(spec, shown) {
    if (!spec.displayUnit)
        return shown;
    return round(shown * spec.displayUnit.factor);
}
/** The unit label to show, which is the display unit when there is one. */
export function displayUnitOf(spec) {
    return spec.displayUnit?.unit ?? spec.unit;
}
/** Kills binary-floating-point dust without imposing a decimal count. */
function round(value) {
    return Math.round(value * 1e6) / 1e6;
}
/** True when anything here should stop a prescription being saved. */
export function hasBlocking(messages) {
    return messages.some((m) => m.severity === 'blocking');
}
// =============================================================================
// Helpers
// =============================================================================
/**
 * True when the exercise asks the patient for nothing — no click, no pointer,
 * no key. They watch, and the only control is the host's stop.
 *
 * The oculomotor exercises all became this on 2026-08-23. One helper rather
 * than four copies of `allowedInputModes.length === 0`, because the screens
 * that ask are asking a question about the exercise, not counting an array.
 */
export function takesNoInput(definition) {
    return definition.allowedInputModes.length === 0;
}
/**
 * What to store in the input-mode column for an exercise that takes no input.
 *
 * The prescription schema requires a value and nothing ever reads this one.
 * Named rather than written as a bare 'mouse' at each call site, so it is
 * obvious that it is a placeholder and not a clinical choice.
 */
export const INPUT_MODE_PLACEHOLDER = 'mouse';
/** Fill in defaults for any parameter the prescription did not set. */
export function withDefaults(definition, provided = {}) {
    const out = {};
    for (const [key, spec] of Object.entries(definition.params)) {
        const given = provided[key];
        out[key] = given === undefined ? spec.default : given;
    }
    return out;
}
/**
 * A stored set of values, with any setting the exercise no longer declares left
 * out.
 *
 * An exercise can RETIRE a setting — saccades dropped its landing tolerance on
 * 2026-08-23 — and every prescription saved before that still carries it.
 * `validateParams` rightly refuses an undeclared value arriving in a save, so
 * without this an old program could be run by the patient but never edited
 * again by the doctor. This is for values being READ from storage. Never apply
 * it to values arriving from a client: that is the case the refusal is for.
 *
 * Nothing stored is changed. The row keeps what was prescribed at the time.
 */
export function currentParams(definition, stored) {
    const current = {};
    for (const key of Object.keys(stored)) {
        const value = stored[key];
        if (key in definition.params && value !== undefined)
            current[key] = value;
    }
    return current;
}
/**
 * Validate parameter values against the declared spec, then run the exercise's
 * own cross-field checks. Used by the server before writing a prescription and
 * by the builder for live feedback — one implementation, both sides.
 *
 * Everything this function generates itself is 'blocking': a value outside its
 * declared range, of the wrong type, missing, or not declared at all is a bug
 * or a stale prescription, never a clinical choice. Only the exercise's own
 * `validate()` can raise an advisory, because only it knows the clinical
 * meaning of the numbers.
 */
export function validateParams(definition, values) {
    const messages = [];
    const block = (param, message) => messages.push({ severity: 'blocking', message, param });
    for (const [key, spec] of Object.entries(definition.params)) {
        const value = values[key];
        if (value === undefined) {
            block(key, `${spec.label}: missing`);
            continue;
        }
        switch (spec.kind) {
            case 'number': {
                if (typeof value !== 'number' || !Number.isFinite(value)) {
                    block(key, `${spec.label}: must be a number`);
                }
                else if (value < spec.min || value > spec.max) {
                    // Stated in the unit the doctor is reading, or the message contradicts
                    // the field it is pointing at.
                    const unit = displayUnitOf(spec);
                    block(key, `${spec.label}: must be between ${toDisplayValue(spec, spec.min)} and ` +
                        `${toDisplayValue(spec, spec.max)}${unit ? ` ${unit}` : ''}`);
                }
                break;
            }
            case 'enum': {
                if (!spec.options.some((o) => o.value === value)) {
                    block(key, `${spec.label}: not an allowed option`);
                }
                break;
            }
            case 'boolean': {
                if (typeof value !== 'boolean')
                    block(key, `${spec.label}: must be true or false`);
                break;
            }
            case 'color': {
                if (typeof value !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(value)) {
                    block(key, `${spec.label}: must be a hex colour like #ffffff`);
                }
                break;
            }
        }
    }
    // Reject anything not declared, so a stale prescription cannot smuggle
    // unknown values into a run.
    for (const key of Object.keys(values)) {
        if (!(key in definition.params))
            block(key, `Unknown parameter: ${key}`);
    }
    // Cross-field checks read several values at once, so they are only meaningful
    // once every individual value is known to be sane.
    if (!hasBlocking(messages) && definition.validate) {
        messages.push(...definition.validate(values));
    }
    return messages;
}
/** Degrees of visual angle to pixels, using the patient's calibration. */
export function degToPx(degrees, pxPerDegree) {
    return degrees * pxPerDegree;
}
/**
 * A run's samples reduced to one value per metric, ready to be written as
 * `ExerciseMetric` rows.
 *
 * Pure and isomorphic, and deliberately here rather than in the server: the
 * rule for combining samples is part of what a metric MEANS (see
 * METRIC_ACCUMULATION), and a second implementation anywhere else would be free
 * to disagree with this one about a patient's accuracy.
 *
 * Unknown metric keys are dropped rather than guessed at. A sample naming a
 * metric this contract has never heard of came from a stale client or a bug,
 * and inventing a summary rule for it would write a number nothing can read.
 */
export function summariseMetrics(samples) {
    const perTrial = new Map();
    const cumulative = new Map();
    for (const sample of samples) {
        if (!(sample.metric in METRIC_ACCUMULATION))
            continue;
        if (typeof sample.value !== 'number' || !Number.isFinite(sample.value))
            continue;
        if (METRIC_ACCUMULATION[sample.metric] === 'cumulative') {
            cumulative.set(sample.metric, sample.value);
            continue;
        }
        const running = perTrial.get(sample.metric) ?? { sum: 0, count: 0 };
        running.sum += sample.value;
        running.count += 1;
        perTrial.set(sample.metric, running);
    }
    const out = {};
    for (const [metric, { sum, count }] of perTrial)
        out[metric] = sum / count;
    for (const [metric, value] of cumulative)
        out[metric] = value;
    return out;
}
//# sourceMappingURL=types.js.map